import { useState } from 'react'
import {
  FileSignature, Building2, Palette, ArrowRight, Check,
  Sparkles, User
} from 'lucide-react'
import { useLocale } from '../contexts/LocaleContext'

interface OnboardingWizardProps {
  onComplete: (data: OnboardingData) => void
  userEmail: string
}

export interface OnboardingData {
  fullName: string
  businessName: string
  businessType: string
  primaryColor: string
}

const COLORS = [
  { id: '#FF69B4', label: 'Pink' },
  { id: '#8b5cf6', label: 'Purple' },
  { id: '#3b82f6', label: 'Blue' },
  { id: '#06b6d4', label: 'Cyan' },
  { id: '#10b981', label: 'Green' },
  { id: '#f59e0b', label: 'Orange' },
  { id: '#ef4444', label: 'Red' },
  { id: '#1e293b', label: 'Dark' },
]

export default function OnboardingWizard({ onComplete, userEmail }: OnboardingWizardProps) {
  const { tx } = useLocale()
  const [step, setStep] = useState(0)
  const [done, setDone] = useState(false)
  const [fullName, setFullName] = useState('')
  const [businessName, setBusinessName] = useState('')
  const [businessType, setBusinessType] = useState('')
  const [primaryColor, setPrimaryColor] = useState('#FF69B4')

  const steps = [
    { title: tx('Welcome', 'ברוכים הבאים'), icon: <Sparkles className="w-6 h-6" /> },
    { title: tx('Personal Details', 'פרטים אישיים'), icon: <User className="w-6 h-6" /> },
    { title: tx('Business Details', 'פרטי עסק'), icon: <Building2 className="w-6 h-6" /> },
    { title: tx('Branding', 'מיתוג'), icon: <Palette className="w-6 h-6" /> },
  ]

  const BUSINESS_TYPES_LOCALIZED = [
    { id: 'freelance', label: tx('Freelance', 'עצמאי') },
    { id: 'agency', label: tx('Agency / Firm', 'סוכנות / משרד') },
    { id: 'consulting', label: tx('Consulting', 'ייעוץ') },
    { id: 'tech', label: tx('Technology', 'טכנולוגיה') },
    { id: 'design', label: tx('Design', 'עיצוב') },
    { id: 'marketing', label: tx('Marketing', 'שיווק') },
    { id: 'construction', label: tx('Construction / Renovation', 'בנייה / שיפוצים') },
    { id: 'other', label: tx('Other', 'אחר') },
  ]

  const canProceed = () => {
    if (step === 0) return true
    if (step === 1) return fullName.trim().length > 0
    if (step === 2) return businessName.trim().length > 0
    if (step === 3) return true
    return true
  }

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1)
    } else {
      setDone(true)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      {/* Gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-slate-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-lg mx-4">
        {/* Completion screen */}
        {done && (
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-10 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {tx("You're all set!", 'הכל מוכן!')}
            </h2>
            <p className="text-slate-300/60 text-sm mb-8">
              {tx("Let's create your first proposal.", 'בואו ניצור את ההצעה הראשונה שלך.')}
            </p>
            <button
              onClick={() => onComplete({ fullName, businessName, businessType, primaryColor })}
              className="inline-flex items-center gap-2 bg-accent-500 text-white px-8 py-3 rounded-xl font-semibold hover:bg-accent-600 transition-all shadow-lg shadow-slate-500/30"
            >
              <span>{tx('Create My First Proposal', 'צור את ההצעה הראשונה שלי')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Wizard */}
        {!done && (
          <>
          {/* Logo */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-accent-500 rounded-2xl shadow-lg shadow-slate-500/30 mb-3">
              <FileSignature className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Quor</h1>
          </div>

          {/* Progress indicator */}
          <div className="flex flex-col items-center gap-2 mb-8">
            <span className="text-xs font-medium text-slate-400/70 tracking-wide uppercase">
              {tx(`Step ${step + 1} of ${steps.length}`, `שלב ${step + 1} מתוך ${steps.length}`)}
            </span>
            <div className="flex items-center gap-2">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? 'w-8 bg-slate-400' :
                    i < step ? 'w-4 bg-slate-500/60' :
                    'w-4 bg-white/20'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Card */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8">
          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br bg-accent-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-slate-300" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">{tx('Welcome to Quor!', 'ברוכים הבאים ל-Quor!')}</h2>
              <p className="text-slate-200/80 mb-3 font-medium">
                {tx("You're 2 minutes away from your first AI-powered proposal.", 'אתה 2 דקות מהצעת המחיר הראשונה שלך מבוססת AI.')}
              </p>
              <p className="text-slate-300/50 text-sm">
                {userEmail}
              </p>
            </div>
          )}

          {/* Step 1: Personal Details */}
          {step === 1 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-1 text-center">{tx('How should we address you?', 'איך לפנות אליך?')}</h2>
              <p className="text-slate-300/50 text-sm text-center mb-6">{tx("We'll use your name to personalize the app.", 'נשתמש בשמך להתאמה אישית של האפליקציה.')}</p>
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">{tx('Full Name', 'שם מלא')}</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoFocus
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-300/50 focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-all"
                  placeholder={tx('John Doe', 'ישראל ישראלי')}
                />
              </div>
            </div>
          )}

          {/* Step 2: Business Details */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-1 text-center">{tx('Tell us about your business', 'ספר לנו על העסק שלך')}</h2>
              <p className="text-slate-300/50 text-sm text-center mb-6">{tx("We'll personalize your proposals for your industry.", 'נתאים את הצעות המחיר שלך לתחום שלך.')}</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">{tx('Business Name', 'שם העסק')}</label>
                  <input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    autoFocus
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-300/50 focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-all"
                    placeholder={tx('Your business name', 'שם העסק שלך')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">{tx('Industry', 'תחום')}</label>
                  <div className="grid grid-cols-2 gap-2">
                    {BUSINESS_TYPES_LOCALIZED.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setBusinessType(type.id)}
                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                          businessType === type.id
                            ? 'bg-slate-500 text-white'
                            : 'bg-white/10 text-slate-200/70 hover:bg-white/15 border border-white/10'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Branding */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-1 text-center">{tx('Choose a brand color', 'בחר צבע מותג')}</h2>
              <p className="text-slate-200/50 text-sm text-center mb-6">
                {tx('Your brand color appears on every proposal you send. You can change it anytime.', 'צבע המותג שלך מופיע בכל הצעת מחיר שתשלח. ניתן לשנות בכל עת.')}
              </p>
              <div className="grid grid-cols-4 gap-3 max-w-xs mx-auto">
                {COLORS.map(color => (
                  <button
                    key={color.id}
                    onClick={() => setPrimaryColor(color.id)}
                    className={`relative w-14 h-14 rounded-xl transition-all mx-auto ${
                      primaryColor === color.id ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.id }}
                  >
                    {primaryColor === color.id && (
                      <Check className="w-5 h-5 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                ))}
              </div>
              {/* Preview */}
              <div className="mt-6 p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: primaryColor }} />
                  <span className="text-white font-medium">{businessName || tx('Your Business', 'העסק שלך')}</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 mb-1">
                  <div className="h-full w-2/3 rounded-full" style={{ backgroundColor: primaryColor }} />
                </div>
                <div className="h-2 w-1/2 rounded-full bg-white/10" />
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            {step > 0 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="text-sm text-slate-300/60 hover:text-white transition-colors"
              >
                {tx('Back', 'חזרה')}
              </button>
            ) : (
              <div />
            )}
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex items-center gap-2 bg-accent-500 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-accent-600 transition-all disabled:opacity-50 shadow-lg shadow-slate-500/30"
            >
              <span>{step === steps.length - 1 ? tx('Create My First Proposal', 'צור את ההצעה הראשונה שלי') : tx('Continue', 'המשך')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          </div>

          {/* Skip */}
          {step > 0 && (
            <div className="text-center mt-3">
              <button
                onClick={() => onComplete({ fullName, businessName, businessType, primaryColor })}
                className="text-xs text-slate-500/50 hover:text-slate-400/60 transition-colors"
              >
                {tx('Skip', 'דלג')}
              </button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  )
}
